import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Bolt,
  CheckCircle,
  Lightbulb,
  Lock,
  Mail,
  Person,
  Phone,
  ShowChart,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import useAuth from "@/hooks/useAuth";
import { extractErrorMessage } from "@/utils/errors";
import type { ModuleCode } from "@/types";

interface RegisterFormValues {
  full_name: string;
  email: string;
  phone_number: string;
  password: string;
  confirm_password: string;
}

const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const PHONE_PATTERN = /^\+?[0-9]{10,15}$/;

// User Onboarding + Module Access (Pilot) -- Option Analyst is the only
// module currently available (mirrors the `modules` table seed); Algo
// Strategy and Investment Ideas are shown so users know they exist but are
// not yet selectable. Registration always submits selected_modules:
// ["OPTION_ANALYST"] -- there is nothing else a user could toggle today.
const PILOT_MODULES: ModuleCode[] = ["OPTION_ANALYST"];

// The reference mockup for this page showed 4 generic placeholder module
// cards (Market Data, Portfolio Analytics, Trading Execution, Compliance &
// Reporting). This app has 3 real pilot modules -- Option Analyst, Algo
// Strategy, Investment Ideas -- so the card-based selection UI is applied
// to those instead of inventing product surfaces that don't exist.
const MODULE_CARDS: Array<{
  code: ModuleCode;
  title: string;
  description: string;
  icon: typeof ShowChart;
  available: boolean;
}> = [
  {
    code: "OPTION_ANALYST",
    title: "Option Analyst",
    description: "Chain analytics & strategy tools",
    icon: ShowChart,
    available: true,
  },
  {
    code: "ALGO_STRATEGY",
    title: "Algo Strategies",
    description: "Automated execution",
    icon: Bolt,
    available: false,
  },
  {
    code: "INVESTMENT_IDEAS",
    title: "Investment Ideas",
    description: "Curated opportunities",
    icon: Lightbulb,
    available: false,
  },
];

// Abstract, unlabeled candlestick backdrop -- decorative only. Deliberately
// carries no numbers/percentages/labels (unlike the reference mockup's
// "+3.378" / "+18.2%" annotations) so nothing here could be mistaken for
// real market data, matching this codebase's existing policy against
// fabricated data in marketing-facing visuals.
function CandlestickBackdrop() {
  const heights = [70, 100, 55, 130, 90, 150, 75, 115, 95, 140, 80, 125, 60, 155, 105, 90, 135, 65, 115, 100];
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: 260,
        opacity: 0.25,
        pointerEvents: "none",
      }}
    >
      <svg width="100%" height="100%" viewBox="0 0 600 260" preserveAspectRatio="none">
        {heights.map((h, i) => (
          <rect
            key={i}
            x={i * 30 + 4}
            y={260 - h}
            width={18}
            height={h}
            fill={i % 3 === 0 ? "#3B82F6" : "#00B386"}
          />
        ))}
      </svg>
    </Box>
  );
}

function BrandLockup() {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          background: "linear-gradient(135deg, #3B82F6, #00B386)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Typography variant="body1" fontWeight={800} color="#fff">
          T
        </Typography>
      </Box>
      <Typography variant="h6" fontWeight={800} color="#fff" letterSpacing={0.5}>
        TEK
        <Box component="span" sx={{ color: "#5EA8F5", fontWeight: 400 }}>
          INVEST
        </Box>
      </Typography>
    </Stack>
  );
}

function BrandingPanel() {
  return (
    <Box
      sx={{
        flex: { md: "0 0 50%" },
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(160deg, #0B192C 0%, #1E293B 100%)",
        color: "#fff",
        px: { xs: 4, sm: 6, md: 8 },
        py: { xs: 6, md: 0 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minHeight: { xs: 260, md: "100vh" },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <BrandLockup />
        <Typography variant="overline" sx={{ color: "#5EA8F5", display: "block", mt: 5, letterSpacing: 1.5 }}>
          Institutional Gateway
        </Typography>
        <Typography variant="h4" fontWeight={800} sx={{ mt: 1 }}>
          Welcome to TekInvest
        </Typography>
        <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.75)", mt: 2, maxWidth: 420 }}>
          Sign up to access advanced options analysis, execution, and strategy tools.
        </Typography>
      </Box>
      <CandlestickBackdrop />
    </Box>
  );
}

export function RegisterPage() {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [verificationPendingEmail, setVerificationPendingEmail] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormValues>();
  const password = watch("password");

  const onSubmit = async (values: RegisterFormValues) => {
    setServerError(null);
    setIsSubmitting(true);
    try {
      const user = await registerUser({
        email: values.email,
        password: values.password,
        full_name: values.full_name,
        phone_number: values.phone_number,
        selected_modules: PILOT_MODULES,
      });
      if (user.is_verified) {
        // Email verification is not required in this environment -- same
        // immediate-login behavior as before this feature shipped.
        setSuccess(true);
        setTimeout(() => navigate("/login", { replace: true }), 1500);
      } else {
        setVerificationPendingEmail(user.email);
      }
    } catch (error) {
      setServerError(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: { xs: "column", md: "row" } }}>
      <BrandingPanel />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          px: { xs: 3, sm: 6, md: 8 },
          py: { xs: 6, md: 4 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 440 }}>
          {verificationPendingEmail ? (
            <Stack spacing={2}>
              <Alert severity="success">
                Account created! We&apos;ve sent a verification link to <strong>{verificationPendingEmail}</strong>.
                Please verify your email before signing in.
              </Alert>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Don&apos;t see it in your inbox? Please check your Spam/Junk folder too — verification
                emails sometimes land there.
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Didn&apos;t get it?{" "}
                <Link
                  component={RouterLink}
                  to={`/resend-verification?email=${encodeURIComponent(verificationPendingEmail)}`}
                >
                  Resend verification email
                </Link>
              </Typography>
              <Link component={RouterLink} to="/login" variant="body2" textAlign="center">
                Back to sign in
              </Link>
            </Stack>
          ) : (
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Typography variant="h5" fontWeight={800}>
                Create Your Account
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                Begin your institutional trading journey.
              </Typography>

              <Stack spacing={2}>
                {serverError && <Alert severity="error">{serverError}</Alert>}
                {success && <Alert severity="success">Account created! Redirecting to login...</Alert>}

                <TextField
                  label="Full Name"
                  fullWidth
                  error={Boolean(errors.full_name)}
                  helperText={errors.full_name?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  {...register("full_name", { required: "Full name is required", minLength: 2 })}
                />
                <TextField
                  label="Institutional Email"
                  type="email"
                  fullWidth
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  {...register("email", { required: "Email is required" })}
                />
                <TextField
                  label="Mobile Number"
                  fullWidth
                  placeholder="e.g. +919876543210"
                  error={Boolean(errors.phone_number)}
                  helperText={errors.phone_number?.message ?? "10-15 digits, optionally prefixed with '+'"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  {...register("phone_number", {
                    required: "Mobile number is required",
                    pattern: { value: PHONE_PATTERN, message: "Enter a valid mobile number" },
                  })}
                />
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  error={Boolean(errors.password)}
                  helperText={
                    errors.password?.message ??
                    "Min 8 characters, with uppercase, lowercase, digit & special character"
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  {...register("password", {
                    required: "Password is required",
                    pattern: {
                      value: PASSWORD_PATTERN,
                      message: "Password does not meet the complexity requirements",
                    },
                  })}
                />
                <TextField
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  fullWidth
                  error={Boolean(errors.confirm_password)}
                  helperText={errors.confirm_password?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowConfirmPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  {...register("confirm_password", {
                    required: "Please confirm your password",
                    validate: (value) => value === password || "Passwords do not match",
                  })}
                />

                <Box>
                  <Typography variant="body2" fontWeight={700} mb={1}>
                    Select Your Modules
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                    {MODULE_CARDS.map((module) => {
                      const Icon = module.icon;
                      return (
                        <Box
                          key={module.code}
                          sx={{
                            position: "relative",
                            border: "1.5px solid",
                            borderColor: module.available ? "primary.main" : "divider",
                            bgcolor: module.available ? "action.selected" : "transparent",
                            borderRadius: 2,
                            p: 1.5,
                            opacity: module.available ? 1 : 0.6,
                          }}
                        >
                          {module.available ? (
                            <CheckCircle
                              fontSize="small"
                              color="primary"
                              sx={{ position: "absolute", top: 8, right: 8 }}
                            />
                          ) : (
                            <Chip
                              label="Coming Soon"
                              size="small"
                              sx={{ position: "absolute", top: 6, right: 6, fontSize: 10, height: 20 }}
                            />
                          )}
                          <Icon fontSize="small" color={module.available ? "primary" : "disabled"} />
                          <Typography variant="body2" fontWeight={700} mt={0.5}>
                            {module.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {module.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  fullWidth
                  sx={{
                    bgcolor: "#0B192C",
                    fontWeight: 700,
                    py: 1.25,
                    "&:hover": { bgcolor: "#132840", boxShadow: "0 0 0 3px rgba(59,130,246,0.35)" },
                  }}
                >
                  {isSubmitting ? "Creating account..." : "Register & Continue"}
                </Button>

                <Stack spacing={0.5} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Already have an account?{" "}
                    <Link component={RouterLink} to="/login">
                      Log In
                    </Link>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    By registering, you agree to our Terms of Service and Privacy Policy.
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default RegisterPage;
